<template>
  <div class="line-drawing">
    <Framer ref="framer" id="framer" class="sec-frame">
      <Frame class="background-layer">
        <svg :width="width" :height="height" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <template v-for="(checkpoint, index) in checkpoints" :key="index">
              <linearGradient :id="`grad${index}`" x1="0%" y1="0%" :x2="gradientDirection.x2" :y2="gradientDirection.y2">
                <stop offset="0%" :style="{ stopColor: checkpoint.gradient.from, stopOpacity: 1 }" />
                <stop offset="100%" :style="{ stopColor: checkpoint.gradient.to, stopOpacity: 1 }" />
              </linearGradient>
            </template>
          </defs>
          <path v-for="(path, index) in paths" :key="index" :d="path.d" :stroke="`url(#grad${index})`" :stroke-width="lineWidth" fill="none" />
        </svg>
      </Frame>
    </Framer>
  </div>
</template>


<script setup lang="ts">

import Framer from "@/components/io/atomic/atoms/Framer.vue";

const props = withDefaults(defineProps<{
  checkpoints: Array<{ id: string; angle: number; gradient: { from: string; to: string } }>,
  width: number,
  height: number,
  lineWidth?: number
}>(), {
  checkpoints: () => {
    return []
  },
  lineWidth: 1 // Default value for line width
})

const framer = ref(null);

const bufferAbove = ref(100);
const bufferBelow = ref(100);
const paths = ref<Array<{ d: string }>>([]);

const gradientDirection = {
  x2: '0%',
  y2: '100%',
};

watch(() => props.checkpoints, async () => {
  await nextTick();
  drawLines();
}, { immediate: true });

watch(() => props.width, async () => {
  await nextTick();
  drawLines();
}, { immediate: true });

watch(() => props.height, async () => {
  await nextTick();
  drawLines();
}, { immediate: true });

watch(() => props.lineWidth, async () => {
  await nextTick();
  drawLines();
}, { immediate: true });

onMounted(async () => {
  await nextTick();
  drawLines();
});

function drawLines() {
  paths.value = []; // Reset paths
  const elements = props.checkpoints.map(cp => document.getElementById(cp.id)?.getBoundingClientRect()).filter(Boolean);

  if (elements.length > 1) {
    const svgRect = document.querySelector('.background-layer')?.getBoundingClientRect();

    if (svgRect) {
      const offsetX = svgRect.left;
      const offsetY = svgRect.top;

      elements.reduce((prevPoint, currPoint, index) => {
        if (!prevPoint || !currPoint) return currPoint;

        const currentPoint = {
          x: prevPoint.left + prevPoint.width / 2 - offsetX,
          y: prevPoint.top + prevPoint.height / 2 - offsetY,
        };

        const nextPoint = {
          x: currPoint.left + currPoint.width / 2 - offsetX,
          y: currPoint.top + currPoint.height / 2 - offsetY,
        };

        const angle = (Math.PI / 180) * props.checkpoints[index].angle;
        const horizontalDistance = Math.abs(nextPoint.x - currentPoint.x);

        /* ── special-case: the two checkpoints lie on (almost) the same x ── */
        if (horizontalDistance < 1) {               // 1-px fuzz for FP drift
          const pathData = `
            M${currentPoint.x},${currentPoint.y}
            V${nextPoint.y - bufferAbove.value}
            L${currentPoint.x + 0.01},${nextPoint.y - bufferAbove.value}  <!-- jog -->
            V${nextPoint.y + bufferBelow.value}
          `;
          paths.value.push({ d: pathData.trim() });
          return currPoint;                         // ← continue with reduce
        }

        /* ── normal (angled) branch ─────────────────────────────────────── */
        const verticalDistance = horizontalDistance / Math.tan(angle);

        // Draw vertical line to the intersection point
        const intersectionPoint = {
          x: currentPoint.x,
          y: nextPoint.y - bufferAbove.value - verticalDistance,
        };

        const pathData = `
          M${currentPoint.x},${currentPoint.y}
          V${intersectionPoint.y}
          L${nextPoint.x},${nextPoint.y - bufferAbove.value}
          V${nextPoint.y + bufferBelow.value}
        `;

        paths.value.push({ d: pathData });

        return currPoint;
      }, null);
    }
  }
}
</script>

<style scoped>
.line-drawing {
  position: relative;
  width: 100%;
  height: 100%;

  .checkpoint {
    width: 100px;
    height: 100px;
    border-radius: 100%;
  }

  .sec-frame {
    height: 100%;

    .background-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}
</style>